/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1738886400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Feb 07 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "appsflyer",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to AppsFlyer",
          "depth": 0,
          "id": "connect-to-appsflyer"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing server-to-server events",
          "depth": 1,
          "id": "syncing-server-to-server-events"
        }, {
          "value": "Syncing audiences",
          "depth": 1,
          "id": "syncing-audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      em: "em",
      h3: "h3",
      h4: "h4",
      pre: "pre",
      span: "span",
      img: "img",
      ol: "ol"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Server-to-server events"
              })
            }), _jsx(_components.td, {
              children: "Sync mobile events that occur outside the app to AppsFlyer"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://dev.appsflyer.com/hc/reference/post_s2s_inappevent",
                children: "Server-to-server events docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audiences"
              })
            }), _jsx(_components.td, {
              children: "Import predefined audience segments as lists into AppsFlyer"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.appsflyer.com/hc/en-us/articles/360013291857-Audiences-import-an-audience#use-the-import-audience-api",
                children: "Import audience docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-appsflyer",
        children: "Connect to AppsFlyer"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "AppsFlyer"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to AppsFlyer by entering the following fields depending on your use case:"]
      }), "\n", _jsx(_components.p, {
        children: "To sync S2S events:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "AppsFlyer V2 S2S key"
          }), " to use AppsFlyer's ", _jsx(_components.code, {
            children: "api3"
          }), " events endpoint. Follow ", _jsx(_components.a, {
            href: "https://support.appsflyer.com/hc/en-us/articles/360004562377-Managing-AppsFlyer-tokens",
            children: "these instructions in AppsFlyer's docs"
          }), " to create a S2S key."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Dev key"
          }), " ", _jsx(_components.em, {
            children: "(legacy)"
          }), " to use AppsFlyer's ", _jsx(_components.code, {
            children: "api2"
          }), " events endpoint. As of December 2023, AppsFlyer has deprecated ", _jsx(_components.code, {
            children: "api2"
          }), ". We recommend updating your configuration to use the S2S key for best performance. If you choose to use a dev key, follow ", _jsx(_components.a, {
            href: "https://support.appsflyer.com/hc/en-us/articles/211719806-App-settings-#sdk-authentication-dev-key",
            children: "these instructions in AppsFlyer's docs"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about upgrading from ", _jsx(_components.code, {
          children: "api2"
        }), " to ", _jsx(_components.code, {
          children: "api3"
        }), ", see AppsFlyer's announcement ", _jsx(_components.a, {
          href: "https://support.appsflyer.com/hc/en-us/articles/20509378973457-Bulletin-Upgrading-the-AppsFlyer-S2S-API",
          children: "here"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "To sync to audiences:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "AppsFlyer V2 API key"
          }), ": You can create a V2 API key by following ", _jsx(_components.a, {
            href: "https://support.appsflyer.com/hc/en-us/articles/360004562377-Managing-API-and-Server-to-server-S2S-tokens#manage-your-tokens",
            children: "these instructions"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You only need to provide credentials for the sync type you intend to use. For example, if you would only like to sync S2S events, you do not need to provide an API key but are required to provide a S2S key."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your AppsFlyer destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the AppsFlyer destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-server-to-server-events",
        children: "Syncing server-to-server events"
      }), "\n", _jsx(_components.p, {
        children: "You can use server-to-server events to send mobile events that occur outside the app from your servers to AppsFlyer."
      }), "\n", _jsx(_components.p, {
        children: "Appsflyer's API requires the following event parameters:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "app_id"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "eventName"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "eventValue"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The sync configuration form ensures all these are set and provides some additional options."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "appsflyer-app-id",
        children: "Appsflyer app ID"
      }), "\n", _jsxs(_components.p, {
        children: ["The app ID is the identifier used in the AppsFlyer dashboard. Insert it exactly as it appears on the dashboard. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model. Hightouch syncs the static or column value as the ", _jsxs(_components.a, {
          href: "https://dev.appsflyer.com/hc/reference/post_s2s_inappevent#path-params",
          children: [_jsx(_components.code, {
            children: "app_id"
          }), " path parameter"]
        }), " the API requires."]
      }), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Providing an event name is required to send an event to the Appsflyer API. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model. Hightouch syncs the static or column value as the ", _jsxs(_components.a, {
          href: "https://dev.appsflyer.com/hc/reference/post_s2s_inappevent#path-params",
          children: [_jsx(_components.code, {
            children: "eventName"
          }), " body parameter"]
        }), " the API requires."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Appsflyer uses the time the event arrives at the server."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you map model columns to event metadata. Some of the metadata fields you can map to include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "AppsFlyer ID"
        }), "\n", _jsx(_components.li, {
          children: "IDFV"
        }), "\n", _jsx(_components.li, {
          children: "IP"
        }), "\n", _jsx(_components.li, {
          children: "Operating system"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The AppsFlyer ID is a required field, so ensure you include that in your mapping."
      }), "\n", _jsx(_components.h4, {
        id: "event-value-mapping",
        children: "Event value mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Each event ", _jsx(_components.em, {
          children: "must"
        }), " contain a JSON ", _jsx(_components.code, {
          children: "eventValue"
        }), ", for example:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"af_revenue\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"6\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " \n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"af_currency\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"USD\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"af_content_type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"wallets\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"af_content_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"15854\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"your_custom_attribute\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"456.123\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you map these fields to create the JSON object the ", _jsx(_components.code, {
          children: "eventValue"
        }), " field expects."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-appsflyer-event-value-mapping.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "custom-mappings",
        children: "Custom mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can also map additional custom data that Hightouch sends to the AppsFlyer platform as JSON using the ", _jsx(_components.code, {
          children: "custom_data"
        }), " field."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audiences",
        children: "Syncing audiences"
      }), "\n", _jsx(_components.p, {
        children: "This sync type lets you update which users belong to an audience.\nYou must use an existing audience in AppsFlyer."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select model columns and the corresponding AppsFlyer fields. You can match on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "App ID"
        }), "\n", _jsx(_components.li, {
          children: "IDFV"
        }), "\n", _jsx(_components.li, {
          children: "IDFA"
        }), "\n", _jsx(_components.li, {
          children: "GAID"
        }), "\n", _jsx(_components.li, {
          children: "OAID"
        }), "\n", _jsx(_components.li, {
          children: "IMEI"
        }), "\n", _jsx(_components.li, {
          children: "CUID"
        }), "\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Phone"
        }), "\n", _jsx(_components.li, {
          children: "Braze ID"
        }), "\n", _jsx(_components.li, {
          children: "Amplitude ID"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You must include the App ID for all audience types.\nDepending on whether you're syncing an Android or iOS audience, you need to include at least one additional identifier."
      }), "\n", _jsx(_components.p, {
        children: "For Android audiences, you must also include at least one of the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "GAID"
        }), "\n", _jsx(_components.li, {
          children: "OAID"
        }), "\n", _jsx(_components.li, {
          children: "IMEI"
        }), "\n", _jsx(_components.li, {
          children: "CUID"
        }), "\n", _jsx(_components.li, {
          children: "Braze ID"
        }), "\n", _jsx(_components.li, {
          children: "Amplitude ID"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For iOS audiences, you must also include at least one of the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "IDFV"
        }), "\n", _jsx(_components.li, {
          children: "CUID"
        }), "\n", _jsx(_components.li, {
          children: "Braze ID"
        }), "\n", _jsx(_components.li, {
          children: "Amplitude ID"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "https://support.appsflyer.com/hc/en-us/articles/360013291857#identifier-hash",
          children: "AppsFlyer's docs"
        }), " on identifiers for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "select-an-existing-audience-or-create-a-new-one",
        children: "Select an existing audience or create a new one"
      }), "\n", _jsxs(_components.p, {
        children: ["You can create a new audience or use an existing one. When creating a new audience, you can optionally enter a name; otherwise, Hightouch defaults to the name of the associated model. To use an existing audience, you need to enter the audience's ", _jsx(_components.code, {
          children: "import_key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To get an audience's ", _jsx(_components.code, {
          children: "import_key"
        }), " follow these instructions:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the AppsFlyer left-side panel, go to ", _jsx(_components.strong, {
            children: "Integration > Audiences"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Click on the name of the audience you want to update."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Import"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Import using API"
          }), " section, choose ", _jsx(_components.strong, {
            children: "Add new devices/users"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy the ", _jsx(_components.code, {
            children: "import_key"
          }), " in the ", _jsx(_components.strong, {
            children: "API request body"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about the ", _jsx(_components.strong, {
          children: "Import Audience API"
        }), ", refer to ", _jsx(_components.a, {
          href: "https://support.appsflyer.com/hc/en-us/articles/360013291857-Audiences-import-an-audience?query=import%20key",
          children: "AppsFlyer's docs"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch automatically hashes fields that require it before sending them to Appsflyer."
      }), "\n", _jsxs(_components.p, {
        children: ["You can disable this behavior in the sync configuration. If disabled, the data from the model should be appropriately normalized and hashed according to ", _jsx(_components.a, {
          href: "https://support.appsflyer.com/hc/en-us/articles/360017807278-Audiences-additional-identifiers",
          children: "Appsflyers's hashing requirements"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when an audience member no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the user in the audience"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Remove the user from the audience"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
